/**
 * <vb-collection> (sections/vb-collection-main.liquid): in-page filtering, sorting and LOAD MORE.
 *
 * Everything in the section is a real link or a GET form, so the page works without JavaScript.
 * This upgrades it:
 * - filter / sort / remove links ([data-vb-nav]) and the price form ([data-vb-price]) fetch the
 *   section through the Section Rendering API and swap the toolbar, the results and the sheet
 *   body; the URL is pushed to history (Back/Forward re-render). Open dropdowns and focus are
 *   restored by their data-vb-key, so keyboard users stay where they were.
 * - LOAD MORE ([data-vb-load-more]) appends the next page's cards to the grid, updates the count
 *   and the progress line, and moves focus to the first new card.
 * - FILTERS opens the sheet as a modal <dialog> (Escape, backdrop click and the close button
 *   close it; focus returns to FILTERS). Dropdowns close on outside click and Escape.
 * Results are announced in a polite live region.
 */

const ABORTED = Symbol('aborted');

class VbCollection extends HTMLElement {
  /** @type {AbortController | null} */
  #fetching = null;
  #listeners = new AbortController();

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.sheet = this.querySelector('[data-vb-sheet]');
    this.status = this.querySelector('[data-vb-status]');
    const signal = this.#listeners.signal;

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    this.addEventListener('toggle', (event) => this.#onToggle(event), { capture: true, signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    document.addEventListener('click', (event) => this.#onOutsideClick(event), { signal });
    window.addEventListener('popstate', () => this.#render(window.location.href, { push: false }), { signal });
    window.addEventListener('pagehide', () => this.#saveState({ scroll: true }), { signal });
    this.#restoreLoaded();

    if (this.sheet) {
      this.sheet.addEventListener('click', (event) => {
        if (event.target === this.sheet) this.#closeSheet();
      }, { signal });
      this.sheet.addEventListener('close', () => this.#afterSheetClose(), { signal });
      // Opened through :target (link followed before the script ran): switch to the modal.
      if (window.location.hash === `#${this.sheet.id}`) {
        history.replaceState(history.state, '', window.location.pathname + window.location.search);
        this.#openSheet();
      }
    }
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#fetching?.abort();
  }

  /* ---------- Events ---------- */

  #onClick(event) {
    if (!(event.target instanceof Element)) return;
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const open = event.target.closest('[data-vb-drawer-open]');
    if (open) {
      event.preventDefault();
      this.opener = open;
      this.#openSheet();
      return;
    }

    const close = event.target.closest('[data-vb-drawer-close]');
    if (close) {
      event.preventDefault();
      this.#closeSheet(close.hasAttribute('data-vb-show-results'));
      return;
    }

    const more = event.target.closest('a[data-vb-load-more]');
    if (more) {
      event.preventDefault();
      this.#loadMore(more);
      return;
    }

    const nav = event.target.closest('a[data-vb-nav]');
    if (nav && nav.href) {
      event.preventDefault();
      this.#render(nav.href, { push: true, key: nav.dataset.vbKey });
    }
  }

  #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-vb-price]')) return;
    event.preventDefault();
    const url = new URL(form.action, window.location.href);
    const params = new URLSearchParams();
    for (const [name, value] of new FormData(form)) {
      if (typeof value === 'string' && value.trim() !== '') params.append(name, value.trim());
    }
    url.search = params.toString();
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement.dataset.vbKey : undefined;
    this.#render(url.toString(), { push: true, key: focused });
  }

  /** One dropdown open at a time. */
  #onToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-vb-dd]') || !details.open) return;
    for (const other of this.querySelectorAll('[data-vb-dd][open]')) {
      if (other !== details) other.open = false;
    }
  }

  #onKeydown(event) {
    // FILTERS and the sheet's close are links with role="button" (so they work without
    // JavaScript through :target): give them the button's Space key.
    if (event.key === ' ' && event.target instanceof Element && event.target.matches('a[role="button"]')) {
      event.preventDefault();
      /** @type {HTMLElement} */ (event.target).click();
      return;
    }
    if (event.key !== 'Escape') return;
    const details = event.target instanceof Element ? event.target.closest('[data-vb-dd][open]') : null;
    if (!details) return;
    event.preventDefault();
    details.open = false;
    details.querySelector('summary')?.focus();
  }

  #onOutsideClick(event) {
    for (const details of this.querySelectorAll('[data-vb-dd][open]')) {
      if (!details.contains(/** @type {Node} */ (event.target))) details.open = false;
    }
  }

  /* ---------- Sheet ---------- */

  #openSheet() {
    if (!this.sheet || this.sheet.open) return;
    for (const details of this.querySelectorAll('[data-vb-dd][open]')) details.open = false;
    this.sheet.showModal();
    document.documentElement.style.setProperty('overflow', 'hidden');
    this.sheet.querySelector('.vb-cm__sheet-close')?.focus();
  }

  #closeSheet(toResults = false) {
    if (!this.sheet) return;
    this.toResults = toResults;
    if (this.sheet.open) this.sheet.close();
    else this.#afterSheetClose();
  }

  #afterSheetClose() {
    document.documentElement.style.removeProperty('overflow');
    if (window.location.hash === `#${this.sheet.id}`) {
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
    }
    if (this.toResults) {
      this.toResults = false;
      const results = this.querySelector('[data-vb-region="results"]');
      results?.scrollIntoView({ block: 'start', behavior: this.#motion() });
      const first = results?.querySelector('.vb-card__link');
      if (first instanceof HTMLElement) {
        first.focus({ preventScroll: true });
        return;
      }
    }
    const opener = this.querySelector('[data-vb-drawer-open]');
    if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
  }

  /* ---------- Rendering ---------- */

  /**
   * @param {string} href
   * @param {{ push?: boolean, key?: string }} [options]
   */
  async #render(href, { push = true, key } = {}) {
    const url = new URL(href, window.location.href);
    url.hash = '';
    url.searchParams.delete('page');

    const openKeys = [...this.querySelectorAll('details[open][data-vb-key]')].map((d) => d.dataset.vbKey);
    const scrollBox = this.sheet?.open ? this.sheet.querySelector('.vb-cm__sheet-body') : null;
    const sheetScroll = scrollBox?.scrollTop ?? 0;

    const doc = await this.#fetchSection(url);
    if (doc === ABORTED) return;
    if (!doc) {
      window.location.assign(url.toString());
      return;
    }

    for (const name of ['toolbar', 'results', 'sheet']) {
      const current = this.querySelector(`[data-vb-region="${name}"]`);
      const next = doc.querySelector(`[data-vb-region="${name}"]`);
      if (current && next) current.replaceWith(next);
    }

    // Re-open what was open; keep the sheet body where it was.
    for (const openKey of openKeys) {
      const details = this.querySelector(`details[data-vb-key="${CSS.escape(openKey)}"]`);
      if (details instanceof HTMLDetailsElement) details.open = true;
    }
    const newBox = this.sheet?.querySelector('.vb-cm__sheet-body');
    if (newBox) newBox.scrollTop = sheetScroll;

    if (key) {
      const target = this.querySelector(`[data-vb-key="${CSS.escape(key)}"]`);
      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
      } else {
        // The control is gone (e.g. a removed filter chip): fall back to its dropdown or FILTERS.
        const fallback = this.sheet?.open
          ? this.sheet.querySelector('.vb-cm__sheet-close')
          : this.querySelector('[data-vb-drawer-open]');
        if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
      }
    }

    this.pagesLoaded = 1;
    if (push) history.pushState({ vbCollection: true }, '', url.toString());
    this.#announce();

    // Keep the top of the grid in view when the page was scrolled past it.
    if (!this.sheet?.open) {
      const toolbar = this.querySelector('[data-vb-region="toolbar"]');
      if (toolbar && toolbar.getBoundingClientRect().top < 0) {
        toolbar.scrollIntoView({ block: 'start', behavior: this.#motion() });
      }
    }
  }

  /** @param {HTMLAnchorElement} link */
  /**
   * @param {HTMLAnchorElement} link
   * @param {{ quiet?: boolean }} [options] - quiet: no focus move, scroll or announcement (restore)
   */
  async #loadMore(link, { quiet = false } = {}) {
    if (link.getAttribute('aria-busy') === 'true') return false;
    link.setAttribute('aria-busy', 'true');
    const label = link.querySelector('.vb-btn__label');
    const idle = label?.textContent ?? '';
    if (label) label.textContent = this.dataset.loadingLabel || idle;

    const doc = await this.#fetchSection(new URL(link.href, window.location.href));
    if (doc === ABORTED) {
      link.removeAttribute('aria-busy');
      if (label) label.textContent = idle;
      return false;
    }
    if (!doc) {
      if (!quiet) window.location.assign(link.href);
      return false;
    }

    const grid = this.querySelector('[data-vb-grid]');
    const items = [...doc.querySelectorAll('[data-vb-item]')];
    const fragment = document.createDocumentFragment();
    for (const item of items) {
      item.classList.add('is-new');
      fragment.append(item);
    }
    grid?.append(fragment);

    const more = this.querySelector('[data-vb-more]');
    const nextMore = doc.querySelector('[data-vb-more]');
    if (more && nextMore) more.replaceWith(nextMore);

    const count = this.querySelector('[data-vb-count]');
    const nextCount = doc.querySelector('[data-vb-count]');
    if (count && nextCount) count.textContent = nextCount.textContent;

    this.pagesLoaded = (this.pagesLoaded ?? 1) + 1;
    this.#saveState();
    if (quiet) return true;

    const firstLink = items[0]?.querySelector('.vb-card__link');
    if (firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: true });
    items[0]?.scrollIntoView({ block: 'nearest', behavior: this.#motion() });
    this.#announce();
    return true;
  }

  /* ---------- Back to the grid after LOAD MORE ---------- */

  get #stateKey() {
    return `vb-cm:${window.location.pathname}${window.location.search}`;
  }

  /** The element that scrolls the page (Horizon scrolls inside .page-wrapper on large screens). */
  get #scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight + 1) {
      const overflow = getComputedStyle(wrapper).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return wrapper;
    }
    return document.scrollingElement ?? document.documentElement;
  }

  /** Remember how many pages are on screen (and the scroll position when leaving the page). */
  #saveState({ scroll = false } = {}) {
    if ((this.pagesLoaded ?? 1) < 2) return;
    try {
      const state = { pages: this.pagesLoaded, y: scroll ? this.#scroller.scrollTop : undefined };
      sessionStorage.setItem(this.#stateKey, JSON.stringify(state));
    } catch {
      /* storage unavailable: Back simply shows the first page */
    }
  }

  /** Coming back (Back/Forward) to a grid that had more pages loaded: load them again, then scroll. */
  async #restoreLoaded() {
    let state = null;
    try {
      state = JSON.parse(sessionStorage.getItem(this.#stateKey) ?? 'null');
    } catch {
      return;
    }
    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType('navigation')[0]);
    if (!state || state.pages < 2 || navigation?.type !== 'back_forward') {
      try {
        sessionStorage.removeItem(this.#stateKey);
      } catch {
        /* ignore */
      }
      return;
    }
    while ((this.pagesLoaded ?? 1) < state.pages) {
      const link = this.querySelector('a[data-vb-load-more]');
      if (!(link instanceof HTMLAnchorElement)) break;
      const ok = await this.#loadMore(link, { quiet: true });
      if (!ok) break;
    }
    if (typeof state.y === 'number') {
      requestAnimationFrame(() => {
        this.#scroller.scrollTop = state.y;
      });
    }
  }

  /**
   * @param {URL} url
   * @returns {Promise<Document | null | typeof ABORTED>}
   */
  async #fetchSection(url) {
    this.#fetching?.abort();
    const controller = new AbortController();
    this.#fetching = controller;
    const results = this.querySelector('[data-vb-region="results"]');
    results?.setAttribute('aria-busy', 'true');

    try {
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', this.sectionId ?? '');
      const response = await fetch(sectionUrl, { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return ABORTED;
      return null;
    } finally {
      if (this.#fetching === controller) {
        this.#fetching = null;
        this.querySelector('[data-vb-region="results"]')?.removeAttribute('aria-busy');
      }
    }
  }

  #announce() {
    if (!this.status) return;
    const text = this.querySelector('.vb-cm__seen')?.textContent?.trim() ?? '';
    this.status.textContent = '';
    requestAnimationFrame(() => {
      if (this.status) this.status.textContent = text;
    });
  }

  /** @returns {ScrollBehavior} */
  #motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
}

if (!customElements.get('vb-collection')) customElements.define('vb-collection', VbCollection);
